import { Link } from "react-router-dom";
import { formatRelativeTime } from "@/lib/format-relative-time";
import type { ScheduleListItem } from "../types";

interface ScheduleListRowProps {
  schedule: ScheduleListItem;
  onDelete: (id: string) => void;
}

export function ScheduleListRow({ schedule, onDelete }: ScheduleListRowProps) {
  const { errors, warnings } = schedule.issueCounts;
  const hasIssues = errors + warnings > 0;
  const syncedLabel = schedule.lastImportedAt
    ? `Synced ${formatRelativeTime(schedule.lastImportedAt)}`
    : "Never synced";

  return (
    <div
      data-testid={`schedule-row-${schedule.id}`}
      className="bg-surface border border-border-subtle rounded-lg p-4 flex items-center justify-between hover:border-brand/50 transition-colors"
    >
      <Link
        to={`/schedules/${schedule.id}`}
        data-testid={`schedule-link-${schedule.id}`}
        className="flex-1 min-w-0"
      >
        <div className="text-sm font-medium text-white truncate">
          {schedule.name}
        </div>
        <div className="text-xs text-text-muted mt-1">
          {schedule.eventCount} {schedule.eventCount === 1 ? "event" : "events"}{" "}
          · {syncedLabel}
        </div>
      </Link>

      <div className="flex items-center gap-3 ml-4">
        {hasIssues && (
          <span
            data-testid={`schedule-issues-${schedule.id}`}
            className="text-xs font-medium"
          >
            {errors > 0 && (
              <span className="text-red-400">
                {errors} {errors === 1 ? "error" : "errors"}
              </span>
            )}
            {errors > 0 && warnings > 0 && (
              <span className="text-text-faint mx-1">·</span>
            )}
            {warnings > 0 && (
              <span className="text-yellow-400">
                {warnings} {warnings === 1 ? "warning" : "warnings"}
              </span>
            )}
          </span>
        )}
        <button
          type="button"
          aria-label={`Delete schedule ${schedule.name}`}
          data-testid={`schedule-delete-${schedule.id}`}
          onClick={(e) => {
            e.stopPropagation();
            const confirmed = window.confirm(
              `Delete "${schedule.name}"? This removes all imported events and validation issues.`,
            );
            if (confirmed) onDelete(schedule.id);
          }}
          className="text-text-faint hover:text-red-400 p-1 rounded transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
